import React from "react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../assets/styles/tags.css";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBin5Line } from "react-icons/ri";
import { getPageTitle } from "../../utils/getPageTitle";
import Button from "../../components/common/button";
import { useViewTagsQuery } from "../../hooks/tags/viewAllTags";
import { useTagStore } from "../../hooks/tags/addTags";
import { useTagUpdate } from "../../hooks/tags/editTags";
import { useEffect } from "react";
import TagModal from "../../components/modal/tagModal";
import { useQueryClient } from "@tanstack/react-query";
import { useDeleteTag } from "../../hooks/tags/deleteTags";
import { toast } from "react-toastify";
import DeleteModal from "../../components/modal/deleteModal";

const Tags = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [selectedTags, setSelectedTags] = useState(null);
  const [mode, setMode] = useState("create"); // create | edit | view
  const queryClient = useQueryClient();

  const [tags, setTags] = useState([]);

  const { data: rawTagsData, isLoading } = useViewTagsQuery();
  const { mutate: updateTag, isUpdating } = useTagUpdate();
  const { mutate: addTags, isPending: isAdding } = useTagStore();
  const { mutate: deleteTags, isPending: isDeleting } = useDeleteTag();

  useEffect(() => {
    if (rawTagsData) {
      const list = rawTagsData?.data || rawTagsData?.tags || rawTagsData || [];
      setTags(list);
    }
  }, [rawTagsData]);

  // Temporary tags for modal (staging area)
  const [modalTags, setModalTags] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenModal = () => {
    setModalTags([]); // Start with empty modal tags
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTagInput("");
    setModalTags([]);
    setSelectedTags(null);
  };

  const handleAddTag = () => {
    if (tagInput.trim()) {
      const newTag = {
        id: Date.now(),
        name: tagInput.trim(),
      };
      setModalTags([...modalTags, newTag]);
      setTagInput("");
    }
  };

  const handleUpdateTags = () => {
    // Add modal tags to main tags when Update is clicked
    setTags([...tags, ...modalTags]);
    handleCloseModal();
  };

  const handleSave = (data) => {
    if (mode === "edit" && selectedTags) {
      const id = selectedTags._id || selectedTags.id;
      updateTag(
        { id, body: data },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            setSelectedTags(null);
            queryClient.invalidateQueries({ queryKey: ["viewTags"] });
          },
        },
      );
    }
    else if (mode === "delete" && selectedTags) {
      const id = selectedTags._id || selectedTags.id;
      if (!id) {
        toast.error("Tag ID not found!");
        return;
      }
      deleteTags(id, {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedTags(null);
          toast.success("Tag deleted successfully!");
          queryClient.invalidateQueries({ queryKey: ["viewTags"] });
        },
        onError: (err) => {
          console.error("Delete failed:", err);
          toast.error(err?.response?.data?.message || "Failed to delete tag");
        }
      });
    }
    else {
      addTags(data, {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedTags(null);
          queryClient.invalidateQueries({ queryKey: ["viewTags"] });
        },
      });
    }
  };

  const handleDelete = (tag) => {
    setSelectedTags(tag);
    setMode("delete");
    setIsModalOpen(true);
  };

  const handleEditTag = (tag) => {
    setSelectedTags(tag);
    setMode("edit");
    setIsModalOpen(true);
  };

  const handleAddTags = () => {
    setSelectedTags(null);
    setMode("create");
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="industry-header">
        <div>
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
          <p className="page-subtitle">
            Organize industries and sub-industries
          </p>
        </div>

        <Button onClick={handleAddTags}>+ Add Tag</Button>
      </div>

      {isLoading && (
        <div style={{ padding: "20px", textAlign: "center" }}>
          Loading tags...
        </div>
      )}

      <div className="body">
        <div className="tags">
          <ul className="tagLists">
            {tags.map((tag) => (
              <li key={tag._id || tag.id}>
                <>
                  {tag.icon} {tag.title || tag.name}
                  <span
                    className={`status-badge ${tag.isActive || tag.status === "Active" || tag.isactive === true ? "status-active" : "status-expired"}`}
                    style={{
                      fontSize: "10px",
                      padding: "2px 8px",
                      marginLeft: "5px",
                    }}
                  >
                    {tag.isActive ||
                    tag.status === "Active" ||
                    tag.isactive === true
                      ? "Active"
                      : "Inactive"}
                  </span>
                  <div className="tag-actions">
                    <button
                      className="edit-tag-btn"
                      onClick={() => handleEditTag(tag)}
                      aria-label="Edit tag"
                    >
                      <FiEdit2 />
                    </button>
                    <button className="remove-tag" aria-label="Delete">
                      <RiDeleteBin5Line
                        onClick={() => handleDelete(tag)}
                        aria-label="Remove tag"
                      />
                    </button>
                  </div>
                </>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <TagModal
        isOpen={isModalOpen && mode !== "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTags(null);
        }}
        tag={selectedTags}
        mode={mode}
        onSave={handleSave}
        isLoading={isAdding || isUpdating}
      />

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTags(null);
        }}
        onConfirm={() => handleSave()}
        title="Delete Tag"
        itemName={selectedTags?.title || selectedTags?.name}
        isLoading={isDeleting}
      />
    </>
  );
};

export default Tags;
